/**
 * Root element for the `/assets` section.
 *
 * Replaces `assetsShell.tsx` — wraps `PanelShell` (the generic section shell)
 * inside `AssetsSelectionProvider` so multi-asset selection state is available
 * to the left column component and the selection panel.
 *
 * The outlet context shape is unchanged — existing
 * `useOutletContext<AssetsShellOutletContext>()` callers keep compiling.
 */
import {
  ColumnData,
  ListCountProvider,
  PanelShell,
  SlidePanelRegistrar,
} from '@gripp/shared-ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AssetComponent } from './assetComponent';
import { AssetsClearLocationButton } from './assetsClearLocationButton';
import { AssetsFilters } from './assetsFilters';
import { AssetsGroupsPicker } from './assetsGroupsPicker';
import { AssetsSelectionPanelHeaderActions } from './assetsSelectionPanel';
import {
  AssetsSelectionProvider,
  useAssetsSelection,
} from './assetsSelectionState';

const ASSETS_SECTION_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetsSection.tsx';
const ASSET_COMPONENT_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetComponent.tsx';
const ASSET_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/routes.tsx';
const ASSETS_ROUTE_PATTERN = '/assets';
const ASSETS_ROUTE_PANEL_KEY = 'assets';

/**
 * Re-exported for backwards compatibility: existing child-route components
 * call `useOutletContext<AssetsShellOutletContext>()` and expect this type.
 * The outlet context shape is unchanged.
 */
export type AssetsShellOutletContext = SlidePanelRegistrar & {
  hasSelection: boolean;
  clearSelection: () => void;
};

export const AssetsSection = () => (
  <AssetsSelectionProvider>
    <AssetsSectionInner />
  </AssetsSelectionProvider>
);

const AssetsSectionInner = () => {
  const { t, i18n } = useTranslation();
  const {
    rowsSelected,
    setRowsSelected,
    rowsSelectedIds,
    selectedGroups,
    selectionStatus,
    setSelectionStatus,
    hasSelection,
    clearSelection,
    handleGroupsChange,
    currentWorkspaceId,
    createGroup,
  } = useAssetsSelection();

  const outletContextExtras = useMemo<{
    hasSelection: boolean;
    clearSelection: () => void;
  }>(() => ({ hasSelection, clearSelection }), [hasSelection, clearSelection]);

  const [count, setCount] = useState<number>();
  const leftColumn: ColumnData = {
    title: 'assets.menu.title',
    count,
    mainComponent: (
      <AssetComponent
        onRowsSelectedChange={setRowsSelected}
        selectedRowIds={rowsSelectedIds}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
        onAssetsCountChange={setCount}
      />
    ),
    headerComponent: <AssetsFilters />,
    disableScroll: true,
    debug: {
      id: 'assets-list',
      titleKey: 'assets.menu.title',
      component: 'AssetComponent',
      componentSource: ASSET_COMPONENT_SOURCE,
      columnSource: ASSETS_SECTION_SOURCE,
      ownerComponent: 'AssetsSection',
      ownerSource: ASSETS_SECTION_SOURCE,
      routePanelKey: ASSETS_ROUTE_PANEL_KEY,
      routePattern: ASSETS_ROUTE_PATTERN,
      routeSource: ASSET_ROUTES_SOURCE,
      source: {
        owner: {
          file: ASSETS_SECTION_SOURCE,
          line: 47,
          symbol: 'AssetsSection',
        },
        column: {
          file: ASSETS_SECTION_SOURCE,
          line: 75,
          symbol: 'leftColumn',
        },
        component: {
          file: ASSET_COMPONENT_SOURCE,
          line: 33,
          symbol: 'AssetComponent',
        },
        route: {
          file: ASSET_ROUTES_SOURCE,
          line: 31,
          symbol: 'assetsRoutes',
        },
      },
      entity: { type: 'assetList' },
    },
  };

  // Fallback right column: shown when no child route claims the slot.
  // A navigated panel (e.g. /assets/:id, /assets/filters) takes priority so a
  // URL change reflects in the UI instead of staying stuck on the selection view.
  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!hasSelection || !currentWorkspaceId) {
      return undefined;
    }

    const title =
      rowsSelected.length === 1
        ? t('assets.topBar.assetSelected')
        : t('assets.topBar.assetsSelected', {
            count: rowsSelected.length,
          });
    i18n.addResource(i18n.language, 'translation', title, title);

    return {
      title,
      mainComponent: (
        <>
          <AssetsGroupsPicker
            selectedGroups={selectedGroups}
            onGroupsChange={handleGroupsChange}
            onCreateGroup={createGroup}
            workspaceId={currentWorkspaceId}
          />
          <AssetsClearLocationButton
            assetIds={rowsSelectedIds}
            workspaceId={currentWorkspaceId}
          />
        </>
      ),
      headerComponent: (
        <AssetsSelectionPanelHeaderActions onReset={clearSelection} />
      ),
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [
    hasSelection,
    currentWorkspaceId,
    rowsSelected,
    rowsSelectedIds,
    selectedGroups,
    handleGroupsChange,
    clearSelection,
    createGroup,
    t,
    i18n,
  ]);

  return (
    <ListCountProvider count={count}>
      <PanelShell
        leftColumn={leftColumn}
        rightColumnOverride={selectionRightColumn}
        outletContextExtras={outletContextExtras}
      />
    </ListCountProvider>
  );
};
